import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'

export { supabase }

export interface AuthState {
  readonly session: Session | null
  readonly user: User | null
  readonly loading: boolean
  readonly initialized: boolean
  setSession: (session: Session | null) => void
  setLoading: (loading: boolean) => void
  setInitialized: () => void
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,
  initialized: false,

  setSession: (session: Session | null): void => {
    const user = session?.user ?? null
    // Attach the signed-in user to every Sentry event (errors, logs, traces).
    // With sendDefaultPii disabled we never ship the raw JWT/cookies — only
    // these explicit fields reach Sentry.
    if (user) {
      Sentry.setUser(user.email ? { id: user.id, email: user.email } : { id: user.id })
    } else {
      Sentry.setUser(null)
    }
    set({
      session,
      user,
      loading: false,
    })
  },

  setLoading: (loading: boolean): void => {
    set({ loading })
  },

  setInitialized: (): void => {
    set({ initialized: true })
  },

  signOut: async (): Promise<void> => {
    await supabase.auth.signOut()
    Sentry.setUser(null)
    set({ session: null, user: null })
  },
}))
